import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { useResilientQuery } from "@/hooks/useResilientQuery";
import { isConvexConfigured } from "@/lib/convex";
import {
  artworkSourceFor,
  backgroundArtworkArgs,
  backgroundScopeFor,
  choosePageBackground,
  defaultPageBackgroundSettings,
  mergeBackgroundPreview,
  needsReleaseContext,
  NEUTRAL_TONE,
  paletteIsLight,
  parseBackgroundPreview,
  PREVIEW_STORAGE_KEY,
  releaseContextRequired,
  sanitizeStoredPreview,
  selectBackgroundImages,
  VISIT_SEED_STORAGE_KEY,
  type PageBackgroundKind,
  type PageBackgroundPlan,
  type PageBackgroundPreview,
  type PageBackgroundSettings,
} from "@/utils/pageBackgrounds";
import { safeGetItem, safeGetJSON, safeRemoveItem, safeSetItem, safeSetJSON } from "@/utils/safeStorage";
import { useArtworkTone } from "./useArtworkTone";
import { useThemePalette } from "./useThemePalette";

export interface BackgroundReleaseRef {
  spotifyAlbumId?: string;
  upc?: string;
  isrc?: string;
  spotifyTrackId?: string;
  name?: string;
}

let lastKnownSettings: PageBackgroundSettings | null = null;

export const SETTINGS_WAIT_MS = 2500;
export const RELEASE_CONTEXT_WAIT_MS = 4000;

function readVisitSeed(): number {
  const stored = Number.parseInt(safeGetItem(VISIT_SEED_STORAGE_KEY, "session") ?? "", 10);
  if (Number.isFinite(stored) && stored >= 0) return stored;
  const fresh = typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function" ? crypto.getRandomValues(new Uint32Array(1))[0] : Date.now() % 4294967296;
  safeSetItem(VISIT_SEED_STORAGE_KEY, String(fresh), "session");
  return fresh;
}

export function usePageBackgroundSettings(enabled: boolean): PageBackgroundSettings | null {
  const result = useResilientQuery(api.siteLook.getPageBackgrounds, enabled && isConvexConfigured ? {} : "skip");
  const live = result.status === "ready" ? result.value : null;
  useEffect(() => {
    if (live) lastKnownSettings = live;
  }, [live]);
  if (!enabled) return null;
  if (!isConvexConfigured || result.status === "error") return lastKnownSettings ?? defaultPageBackgroundSettings();
  return live ?? lastKnownSettings;
}

export function useBackgroundPreview(enabled: boolean, search: string): PageBackgroundPreview | null {
  const fromUrl = useMemo(() => parseBackgroundPreview(search), [search]);
  const preview = useMemo(() => {
    if (!enabled) return null;
    return mergeBackgroundPreview(sanitizeStoredPreview(safeGetJSON(PREVIEW_STORAGE_KEY, "session")), fromUrl);
  }, [enabled, fromUrl]);
  useEffect(() => {
    if (!enabled) return;
    if (fromUrl.clear) safeRemoveItem(PREVIEW_STORAGE_KEY, "session");
    else if (preview && (fromUrl.style || fromUrl.color)) safeSetJSON(PREVIEW_STORAGE_KEY, { style: preview.style, color: preview.color }, "session");
  }, [enabled, fromUrl, preview]);
  return preview;
}

export interface PageBackgroundPlanInput {
  kind: PageBackgroundKind;
  artistSlug: string | null | undefined;
  releaseKey?: string | null;
  release?: BackgroundReleaseRef;
  coverUrl?: string | null;
}

export function usePageBackgroundPlan(input: PageBackgroundPlanInput): PageBackgroundPlan | null | undefined {
  const scope = backgroundScopeFor(input.artistSlug);
  const enabled = scope === "site-look";
  const liveSettings = usePageBackgroundSettings(enabled);
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    if (!enabled || liveSettings) return;
    const timer = window.setTimeout(() => setWaitedOut(true), SETTINGS_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, liveSettings]);
  const location = useLocation();
  const preview = useBackgroundPreview(enabled, location.search);
  const settings = liveSettings ?? (waitedOut || preview ? defaultPageBackgroundSettings() : null);
  const [nowMs] = useState(() => Date.now());

  const base = settings ? { kind: input.kind, settings, preview, releaseKey: input.releaseKey } : null;
  const wantsContext = Boolean(enabled && base && needsReleaseContext(base));
  const requiresContext = Boolean(enabled && base && releaseContextRequired(base));
  const artworkArgs = useMemo(() => backgroundArtworkArgs(input.kind, input.release), [input.kind, input.release?.spotifyAlbumId, input.release?.upc, input.release?.isrc, input.release?.spotifyTrackId, input.release?.name]);
  const artwork = useResilientQuery(api.siteLook.backgroundArtwork, wantsContext && isConvexConfigured ? artworkArgs : "skip");
  const tone = useArtworkTone(requiresContext ? input.coverUrl : null);
  const theme = useThemePalette(wantsContext);
  const [contextWaitedOut, setContextWaitedOut] = useState(false);
  useEffect(() => {
    if (!requiresContext) return;
    const timer = window.setTimeout(() => setContextWaitedOut(true), RELEASE_CONTEXT_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [requiresContext]);

  const imageCount = useMemo(() => {
    if (!wantsContext) return undefined;
    const coverCandidates = artworkSourceFor(input.kind) === "release" && input.coverUrl ? [{ url: input.coverUrl, source: "cover" as const }] : [];
    if (artwork.status === "ready") return selectBackgroundImages([...artwork.value.images, ...coverCandidates]).length;
    if (artwork.status === "error" || !isConvexConfigured || contextWaitedOut) return selectBackgroundImages(coverCandidates).length;
    return undefined;
  }, [wantsContext, artwork, input.coverUrl, input.kind, contextWaitedOut]);

  const visitSeed = useMemo(() => (enabled && settings?.rotateBy === "visit" ? readVisitSeed() : undefined), [enabled, settings?.rotateBy]);
  const paletteLight = paletteIsLight(theme);

  return useMemo(() => {
    if (!enabled) return null;
    if (!settings) return undefined;
    const effectiveTone = tone ?? (contextWaitedOut || !input.coverUrl ? NEUTRAL_TONE : undefined);
    if (requiresContext && (imageCount === undefined || effectiveTone === undefined)) return undefined;
    const release = wantsContext && imageCount !== undefined ? { imageCount, paletteLight, tone: effectiveTone ?? NEUTRAL_TONE } : null;
    return choosePageBackground({ kind: input.kind, settings, preview, releaseKey: input.releaseKey, release, nowMs, visitSeed });
  }, [enabled, settings, preview, tone, contextWaitedOut, input.coverUrl, requiresContext, imageCount, wantsContext, paletteLight, input.kind, input.releaseKey, nowMs, visitSeed]);
}
